'use client';
import {useEffect, useMemo, useRef, useState} from 'react';
import {sanitizeRichText} from '../shared/utils/sanitize';

export function useClampOverflow(html: string, collapsible: boolean) {
  const proseRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const safe = useMemo(() => sanitizeRichText(html), [html]);
  const clamp = collapsible && !expanded;

  // Only measure while clamped: expanding removes the clamp (scrollHeight ===
  // clientHeight), so we keep the last "overflowing" reading to preserve the
  // Read less affordance. CSS does the truncation; this only gates the toggle.
  useEffect(() => {
    if (!collapsible) {
      setOverflowing(false);
      return;
    }
    const proseElement = proseRef.current;
    if (!proseElement || !clamp) {
      return;
    }
    const measure = () => {
      setOverflowing(proseElement.scrollHeight - 1 > proseElement.clientHeight);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(proseElement);
    return () => {
      observer.disconnect();
    };
  }, [collapsible, clamp, safe]);

  const toggle = () => {
    setExpanded(isExpanded => !isExpanded);
  };

  return {proseRef, safe, clamp, overflowing, expanded, toggle};
}
